/**
 * Черновик автоматического разбора: то, что вернул конвейер, плюс правки
 * человека. Отдельный модуль, потому что логика чистая и её удобно держать
 * в стороне от компонентов (docs/ANALYSIS.md).
 *
 * Главное правило: результат разбора — не готовая разметка, а предположение.
 * Здесь же живут признаки, по которым интерфейс подсвечивает сомнительные
 * строки.
 */

import type {
  AnalysisApplyLine,
  AnalysisLine,
  AnalysisLineKind,
  AnalysisResult,
  AnalysisRoleAssignment,
  AnalysisStage,
} from '../api/types'

/** Где лежит инструкция по запуску тяжёлого сервиса. */
export const ANALYZER_README = 'analyzer/README.md'

export type StageInfo = { id: AnalysisStage; label: string; hint: string }

/** Порядок этапов фиксирован контрактом (docs/ANALYSIS.md, «Этапы конвейера»). */
export const ANALYSIS_STAGES: readonly StageInfo[] = [
  { id: 'extract', label: 'Извлечение звука', hint: 'ffmpeg достаёт дорожку из видео' },
  {
    id: 'separate',
    label: 'Разделение дорожек',
    hint: 'Demucs делит звук на M&E и реплики; самый долгий этап вместе с расшифровкой',
  },
  {
    id: 'diarize',
    label: 'Диаризация',
    hint: 'кто когда говорит; похожие голоса модель путает',
  },
  {
    id: 'transcribe',
    label: 'Расшифровка',
    hint: 'Whisper по дорожке реплик; на именах и шёпоте ошибается',
  },
  { id: 'align', label: 'Сшивка', hint: 'сегменты речи получают говорящих по перекрытию' },
  { id: 'translate', label: 'Перевод', hint: 'локальная модель, подстрочник с контекстом' },
]

const STAGE_LABELS = new Map<string, string>(ANALYSIS_STAGES.map((s) => [s.id, s.label]))

/** Индекс этапа по идентификатору; -1 — этап неизвестен. */
export function stageIndexOf(stage: string): number {
  return ANALYSIS_STAGES.findIndex((item) => item.id === stage)
}

export function stageLabel(stage: string): string {
  return STAGE_LABELS.get(stage) ?? (stage || 'подготовка')
}

// ------------------------------------------------------------- уверенность

/** Ниже — реплику стоит проверить. */
export const CONFIDENCE_LOW = 0.6
/** Ниже — почти наверняка мусор. */
export const CONFIDENCE_BAD = 0.4

export type ConfidenceLevel = 'unknown' | 'bad' | 'low' | 'ok'

export function confidenceLevel(value: number | null): ConfidenceLevel {
  if (value === null || !Number.isFinite(value)) return 'unknown'
  if (value < CONFIDENCE_BAD) return 'bad'
  if (value < CONFIDENCE_LOW) return 'low'
  return 'ok'
}

// ----------------------------------------------------------------- черновик

export type DraftLine = {
  /** Локальный ключ строки: у результата разбора нет идентификаторов. */
  key: string
  start: number
  end: number
  /** Текущий говорящий; меняется при объединении и вручную. '' — не назначен. */
  speaker: string
  /** Как назвала диаризация — чтобы объединение можно было откатить. */
  speakerRaw: string
  text: string
  textOriginal: string
  confidence: number | null
  /**
   * 'nonverbal' — крик, стон, дыхание (docs/SEGMENTATION.md). Слов там нет,
   * и пустой текст здесь не ошибка конвейера, а сам смысл строки.
   */
  kind: AnalysisLineKind
}

export type DraftRole = {
  /** Говорящий-владелец роли; он же ключ в payload применения. */
  speaker: string
  name: string
  color: string
}

export type AnalysisDraft = {
  lines: DraftLine[]
  roles: DraftRole[]
  language: string
  duration: number
  diarizer: string
  /** Сервис отдал дорожки me/dialogue — значит, их можно создать в проекте. */
  hasTracks: boolean
  /** Просили ли перевод: без него пустой `text` — норма, а не ошибка. */
  translated: boolean
}

export const SPEAKER_PALETTE: readonly string[] = [
  '#E8734A',
  '#6d8bff',
  '#34D399',
  '#FBBF24',
  '#F472B6',
  '#22D3EE',
  '#A3E635',
  '#8B5CF6',
]

export function placeholderRoleName(index: number): string {
  return `Говорящий ${index + 1}`
}

const PLACEHOLDER_RE = /^Говорящий \d+$/
const RAW_SPEAKER_RE = /^SPEAKER[_-]?\d+$/i

/** true — имя ещё не человеческое: либо заглушка, либо метка диаризации. */
export function isPlaceholderName(name: string): boolean {
  const trimmed = name.trim()
  if (!trimmed) return true
  return PLACEHOLDER_RE.test(trimmed) || RAW_SPEAKER_RE.test(trimmed)
}

/** Запасной счётчик: без crypto.randomUUID две строки одной миллисекунды
 *  получили бы одинаковый ключ. */
let keySequence = 0

function makeKey(index: number): string {
  const uuid = globalThis.crypto?.randomUUID?.()
  keySequence += 1
  return uuid ?? `draft-${Date.now()}-${index}-${keySequence}`
}

function num(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback
}

function str(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

/**
 * Пометка вместо текста у невербальной реплики. Пустая реплика в игровом
 * режиме выглядит поломкой, а пометка объясняет актёру, что здесь надо
 * не читать, а кричать (docs/SEGMENTATION.md, «Формат результата»).
 * Она подставляется в черновик, чтобы человек мог её поправить.
 */
export const NONVERBAL_TEXT = '[без слов]'

function normalizeKind(raw: unknown): AnalysisLineKind {
  return raw === 'nonverbal' ? 'nonverbal' : 'speech'
}

function normalizeLine(raw: AnalysisLine, index: number): DraftLine | null {
  const start = Math.max(0, num(raw.start, NaN))
  const end = num(raw.end, NaN)
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return null
  const speaker = str(raw.speaker).trim()
  const confidence =
    typeof raw.confidence === 'number' && Number.isFinite(raw.confidence) ? raw.confidence : null
  const kind = normalizeKind(raw.kind)
  const text = str(raw.text)
  return {
    key: makeKey(index),
    start,
    end,
    speaker,
    speakerRaw: speaker,
    // Невербальную строку сразу показываем с пометкой: так её видно в таблице
    // и правится она как обычный текст.
    text: kind === 'nonverbal' && !text.trim() ? NONVERBAL_TEXT : text,
    textOriginal: str(raw.textOriginal),
    confidence,
    kind,
  }
}

/**
 * Превращает ответ сервиса в черновик. Данные приходят из чужого процесса,
 * поэтому всё проверяется: битая строка отбрасывается, а не роняет экран.
 */
export function draftFromResult(result: AnalysisResult, translated: boolean): AnalysisDraft {
  const rawLines = Array.isArray(result.lines) ? result.lines : []
  const lines = rawLines
    .map((line, index) => normalizeLine(line, index))
    .filter((line): line is DraftLine => line !== null)
    .sort((a, b) => a.start - b.start || a.end - b.end)

  const speakers: string[] = []
  const fromResult = Array.isArray(result.speakers) ? result.speakers : []
  for (const speaker of fromResult) {
    const id = str(speaker.id).trim()
    if (id && !speakers.includes(id)) speakers.push(id)
  }
  for (const line of lines) {
    if (line.speaker && !speakers.includes(line.speaker)) speakers.push(line.speaker)
  }

  const roles: DraftRole[] = speakers.map((speaker, index) => ({
    speaker,
    name: placeholderRoleName(index),
    color: SPEAKER_PALETTE[index % SPEAKER_PALETTE.length] as string,
  }))

  const tracks = result.tracks ?? {}
  return {
    lines,
    roles,
    language: str(result.language),
    duration: num(result.duration, 0),
    diarizer: str(result.diarizer),
    hasTracks: Boolean(tracks.me || tracks.dialogue),
    translated,
  }
}

// ------------------------------------------------------------- статистика

export type SpeakerStats = {
  speaker: string
  /** Секунды речи по текущему черновику, а не по исходному ответу. */
  speech: number
  lines: number
  /** Первая реплика — по ней человек и опознаёт персонажа. */
  first: DraftLine | null
  /** Из каких меток диаризации собран говорящий. */
  merged: string[]
}

export function speakerStats(lines: DraftLine[], speaker: string): SpeakerStats {
  let speech = 0
  let count = 0
  let first: DraftLine | null = null
  const merged: string[] = []
  for (const line of lines) {
    if (line.speaker !== speaker) continue
    speech += Math.max(0, line.end - line.start)
    count += 1
    if (!first || line.start < first.start) first = line
    if (line.speakerRaw && !merged.includes(line.speakerRaw)) merged.push(line.speakerRaw)
  }
  return { speaker, speech, lines: count, first, merged }
}

// ------------------------------------------------------------- правки

export function updateLine(
  draft: AnalysisDraft,
  key: string,
  patch: Partial<Omit<DraftLine, 'key' | 'speakerRaw'>>,
): AnalysisDraft {
  return {
    ...draft,
    lines: draft.lines.map((line) => (line.key === key ? { ...line, ...patch } : line)),
  }
}

export function removeLine(draft: AnalysisDraft, key: string): AnalysisDraft {
  return { ...draft, lines: draft.lines.filter((line) => line.key !== key) }
}

export function updateRole(
  draft: AnalysisDraft,
  speaker: string,
  patch: Partial<Omit<DraftRole, 'speaker'>>,
): AnalysisDraft {
  return {
    ...draft,
    roles: draft.roles.map((role) => (role.speaker === speaker ? { ...role, ...patch } : role)),
  }
}

/**
 * Объединяет двух говорящих в одну роль. Диаризация регулярно дробит один
 * голос на несколько меток, и это самая частая правка.
 *
 * Реализовано переносом реплик, а не двумя ролями с одинаковым именем:
 * так payload применения остаётся однозначным. Исходная метка живёт
 * в `speakerRaw`, поэтому объединение обратимо.
 */
export function mergeSpeakers(draft: AnalysisDraft, from: string, into: string): AnalysisDraft {
  if (!from || !into || from === into) return draft
  if (!draft.roles.some((role) => role.speaker === into)) return draft
  return {
    ...draft,
    lines: draft.lines.map((line) => (line.speaker === from ? { ...line, speaker: into } : line)),
    roles: draft.roles.filter((role) => role.speaker !== from),
  }
}

/** Возвращает объединённых говорящих обратно к меткам диаризации. */
export function splitSpeaker(draft: AnalysisDraft, speaker: string): AnalysisDraft {
  const restored: string[] = []
  const lines = draft.lines.map((line) => {
    if (line.speaker !== speaker || !line.speakerRaw || line.speakerRaw === speaker) return line
    if (!restored.includes(line.speakerRaw)) restored.push(line.speakerRaw)
    return { ...line, speaker: line.speakerRaw }
  })
  if (restored.length === 0) return draft
  const used = draft.roles.length
  const roles = [
    ...draft.roles,
    ...restored.map((id, index) => ({
      speaker: id,
      name: placeholderRoleName(used + index),
      color: SPEAKER_PALETTE[(used + index) % SPEAKER_PALETTE.length] as string,
    })),
  ]
  return { ...draft, lines, roles }
}

// ------------------------------------------------- создание и объединение

/**
 * Новая строка черновика — для реплики, которую конвейер пропустил
 * (docs/SEGMENTATION.md, «Объединение и создание реплик»).
 */
export function makeDraftLine(start: number, end: number, speaker: string): DraftLine {
  return {
    key: makeKey(0),
    start,
    end,
    speaker,
    speakerRaw: '',
    text: '',
    textOriginal: '',
    confidence: null,
    kind: 'speech',
  }
}

/** Вставляет строку, сохраняя порядок по таймкоду. */
export function insertLine(draft: AnalysisDraft, line: DraftLine): AnalysisDraft {
  return {
    ...draft,
    lines: [...draft.lines, line].sort((a, b) => a.start - b.start || a.end - b.end),
  }
}

/**
 * Идут ли строки подряд. Объединять можно только соседей: дыра между ними
 * означала бы, что в одну реплику склеиваются куски из разных мест сцены.
 */
export function areAdjacent(draft: AnalysisDraft, keys: ReadonlySet<string>): boolean {
  if (keys.size < 2) return false
  const indexes: number[] = []
  draft.lines.forEach((line, index) => {
    if (keys.has(line.key)) indexes.push(index)
  })
  if (indexes.length !== keys.size) return false
  const first = indexes[0] as number
  return indexes.every((value, offset) => value === first + offset)
}

function joinText(parts: readonly string[]): string {
  return parts
    .map((part) => part.trim())
    .filter((part) => part.length > 0)
    .join(' ')
}

/**
 * Сливает подряд идущие строки в одну: `start` первой, `end` последней,
 * тексты через пробел. Говорящий задаётся явно — если роли разные, интерфейс
 * обязан спросить, чью оставить (docs/SEGMENTATION.md).
 */
export function mergeLines(
  draft: AnalysisDraft,
  keys: ReadonlySet<string>,
  speaker: string,
): AnalysisDraft {
  if (!areAdjacent(draft, keys)) return draft
  const group = draft.lines.filter((line) => keys.has(line.key))
  const first = group[0]
  const last = group[group.length - 1]
  if (!first || !last) return draft
  const confidences = group
    .map((line) => line.confidence)
    .filter((value): value is number => value !== null)
  const merged: DraftLine = {
    ...first,
    speaker,
    start: first.start,
    end: last.end,
    text: joinText(group.map((line) => line.text)),
    textOriginal: joinText(group.map((line) => line.textOriginal)),
    // Уверенность объединения — по худшему куску: он и определяет, стоит ли
    // строку перечитывать.
    confidence: confidences.length > 0 ? Math.min(...confidences) : null,
    // Речь плюс крик — всё-таки речь: текст в реплике есть.
    kind: group.some((line) => line.kind === 'speech') ? 'speech' : 'nonverbal',
  }
  const lines: DraftLine[] = []
  for (const line of draft.lines) {
    if (line.key === first.key) lines.push(merged)
    else if (!keys.has(line.key)) lines.push(line)
  }
  return { ...draft, lines }
}

/** Смена говорящего у одной реплики — включая «без роли». */
export function setLineSpeaker(draft: AnalysisDraft, key: string, speaker: string): AnalysisDraft {
  return {
    ...draft,
    lines: draft.lines.map((line) => (line.key === key ? { ...line, speaker } : line)),
  }
}

// ------------------------------------------------------------- признаки

export type LineFlag = 'lowConfidence' | 'noSpeaker' | 'emptyText' | 'noTranslation' | 'short' | 'overlap'

export const FLAG_LABELS: Record<LineFlag, string> = {
  lowConfidence: 'низкая уверенность',
  noSpeaker: 'говорящий не определён',
  emptyText: 'пустой текст',
  noTranslation: 'перевод совпадает с оригиналом',
  short: 'короче 0,4 с',
  overlap: 'наложение на предыдущую',
}

/** Реплика короче этого — почти всегда обрывок (docs/ANALYSIS.md, «Сшивка»). */
const MIN_SANE_DURATION = 0.4

export function lineFlags(
  line: DraftLine,
  previous: DraftLine | undefined,
  translated: boolean,
): LineFlag[] {
  const flags: LineFlag[] = []
  const level = confidenceLevel(line.confidence)
  if (level === 'low' || level === 'bad') flags.push('lowConfidence')
  if (!line.speaker) flags.push('noSpeaker')
  // Невербальная строка живёт без слов и бывает короткой — это её нормальное
  // состояние, а не признак ошибки (docs/SEGMENTATION.md).
  const nonverbal = line.kind === 'nonverbal'
  if (!nonverbal && !line.text.trim() && !line.textOriginal.trim()) flags.push('emptyText')
  else if (
    !nonverbal &&
    translated &&
    line.textOriginal.trim() &&
    line.text.trim() === line.textOriginal.trim()
  ) {
    flags.push('noTranslation')
  }
  if (!nonverbal && line.end - line.start < MIN_SANE_DURATION) flags.push('short')
  if (previous && line.start < previous.end - 0.001) flags.push('overlap')
  return flags
}

export function draftFlags(draft: AnalysisDraft): Map<string, LineFlag[]> {
  const map = new Map<string, LineFlag[]>()
  draft.lines.forEach((line, index) => {
    map.set(line.key, lineFlags(line, draft.lines[index - 1], draft.translated))
  })
  return map
}

// ------------------------------------------------------------- применение

export type ApplyPlan = {
  roles: AnalysisRoleAssignment[]
  lines: AnalysisApplyLine[]
  /** Сколько строк отброшено как непригодные — показываем перед применением. */
  dropped: number
  unnamed: number
}

/**
 * Готовит payload применения: сортирует, обрезает по длительности видео,
 * выкидывает вырожденные строки. Ограничение сервера — end <= duration + 1
 * (docs/API.md, «Реплики»).
 */
export function buildApplyPlan(draft: AnalysisDraft, videoDuration: number): ApplyPlan {
  const limit = videoDuration > 0 ? videoDuration + 1 : Number.POSITIVE_INFINITY
  const usedSpeakers = new Set<string>()
  const lines: AnalysisApplyLine[] = []
  let dropped = 0

  for (const line of [...draft.lines].sort((a, b) => a.start - b.start || a.end - b.end)) {
    const start = Number(Math.max(0, line.start).toFixed(2))
    const end = Number(Math.min(line.end, limit).toFixed(2))
    if (!(end > start)) {
      dropped += 1
      continue
    }
    if (line.speaker) usedSpeakers.add(line.speaker)
    const text = line.text.trim()
    const textOriginal = line.textOriginal.trim()
    lines.push({
      start,
      end,
      speaker: line.speaker,
      // Пометку пользователь мог стереть — но пустая реплика в игровом режиме
      // выглядит поломкой, поэтому у невербальной строки она возвращается.
      text: text || (line.kind === 'nonverbal' && !textOriginal ? NONVERBAL_TEXT : ''),
      textOriginal,
    })
  }

  const roles = draft.roles
    .filter((role) => usedSpeakers.has(role.speaker))
    .map((role, index) => ({
      speaker: role.speaker,
      name: role.name.trim() || placeholderRoleName(index),
      color: role.color,
    }))

  const unnamed = roles.filter((role) => isPlaceholderName(role.name)).length
  return { roles, lines, dropped, unnamed }
}
